"use client"

import { useState } from "react"
import {
  CheckEligibilityRequest,
  ConfirmThirdPartyOtpRequest,
  StartClaimProcessRequest,
} from "../../../api/hcsraCustomApi"
import {
  checkThirdPartyEligibility,
  confirmThirdPartyOtp,
  startThirdPartyClaimProcess,
} from "../../../api/hcsraProxyApi" // Updated to use proxy API

// Form state types
export type FormState =
  | "idle"
  | "checking"
  | "carDetailsSubmitted"
  | "accidentFormSubmitted"
  | "otpSent"
  | "otpVerifying"
  | "otpError"
  | "otpExpired"
  | "otpVerified"
  | "success"
  | "error"
  | "carNotFound"

export interface CarDetails {
  insuranceType: string
  agentName: string
  agentNumber: number
  policyNumber: number
  phoneNumber: string
  openClaims: string
  thirdPartyEligibilityClaimCheckId: string
}

export interface AccidentReportDetails {
  privateName: string
  sureName: string
  identityNumber: string
  email: string
  mobile: string
  insuranceCompanyName: string
  policyNumberThirdParty: string
  eventDescription: string
  damageDescription: string
}

export function useThirdPartyCoverageFlow() {
  // Form state management
  const [formState, setFormState] = useState<FormState>("idle")
  const [carDetails, setCarDetails] = useState<CarDetails | null>(null)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otpError, setOtpError] = useState<string | null>(null)
  const [carRepaired, setCarRepaired] = useState<boolean | null>(null)
  const [accidentReportData, setAccidentReportData] =
    useState<AccidentReportDetails | null>(null)
  const [licenseNumber, setLicenseNumber] = useState("")
  const [eventDate, setEventDate] = useState("")
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [otpAttempts, setOtpAttempts] = useState<number>(0)

  // Form submission handlers
  const handleCarDetailsSubmit = async (details: {
    licensePlateNumber: string
    eventDate: string
    notFindingCarText: string
  }) => {
    try {
      setFormState("checking")
      setLicenseNumber(details.licensePlateNumber)
      setEventDate(details.eventDate)
      const eligibilityRequest: CheckEligibilityRequest = {
        eventDate: details.eventDate,
        licensePlateNumber: details.licensePlateNumber,
      }

      const response = await checkThirdPartyEligibility(eligibilityRequest)
      if (response?.body?.isInsuredInHcsra) {
        setCarDetails({
          insuranceType: response.body.productName || "מקיף",
          agentName: response.body.agentName || "לא ידוע",
          agentNumber: response.body.agentNumber,
          policyNumber: response.body.policyNumber,
          phoneNumber: response.body.policyHolderPhoneNumber || "",
          openClaims:
            response.body.claimNumber?.toString() || "אין תביעות פתוחות",
          thirdPartyEligibilityClaimCheckId:
            response.body.thirdPartyEligibilityClaimCheckId,
        })
        setStatusMessage(null)
        setFormState("carDetailsSubmitted")
      } else if (response?.error) {
        setStatusMessage(details?.notFindingCarText || "הרכב אינו מבוטח בהכשרה")
        setFormState("carNotFound")
      } else {
        setStatusMessage(
          response?.body?.statusMessage ||
            details?.notFindingCarText ||
            "הרכב אינו מבוטח בהכשרה",
        )
        setFormState("carNotFound")
      }
    } catch (error) {
      console.error("Error checking eligibility:", error)
      setFormState("error")
    }
  }

  const handleInsuranceDetailsSubmit = (isRepaired: boolean) => {
    setCarRepaired(isRepaired)
    setFormState("accidentFormSubmitted")
  }

  const handleAccidentReportSubmit = async (
    reportData: AccidentReportDetails,
  ) => {
    try {
      setAccidentReportData(reportData)
      setFormState("otpSent")

      if (!carDetails?.thirdPartyEligibilityClaimCheckId) {
        throw new Error("Missing claim check ID")
      }

      const startClaimRequest: StartClaimProcessRequest = {
        damageDescription: reportData.damageDescription,
        email: reportData.email,
        eventDescription: reportData.eventDescription,
        identityNumber: reportData.identityNumber,
        insuranceCompanyName: reportData.insuranceCompanyName,
        licenseNumber,
        mobile: reportData.mobile,
        policyNumberThirdParty: reportData.policyNumberThirdParty,
        privateName: reportData.privateName,
        sureName: reportData.sureName,
        thirdPartyEligibilityClaimCheckId:
          carDetails.thirdPartyEligibilityClaimCheckId,
      }

      const response = await startThirdPartyClaimProcess(startClaimRequest)

      if (response.error) {
        throw new Error("Failed to start claim process")
      }

      setPhoneNumber(reportData.mobile) // Use the phone number from the accident report
    } catch (error) {
      console.error("Error submitting accident report:", error)
      setFormState("error")
    }
  }

  const handleOtpSubmit = async (otp: string) => {
    try {
      setFormState("otpVerifying")

      if (!carDetails?.thirdPartyEligibilityClaimCheckId) {
        throw new Error("Missing claim check ID")
      }

      const confirmOtpRequest: ConfirmThirdPartyOtpRequest = {
        otpCode: otp,
        thirdPartyEligibilityClaimCheckId:
          carDetails.thirdPartyEligibilityClaimCheckId,
      }

      const response = await confirmThirdPartyOtp(confirmOtpRequest)

      if (response.error || !response.body) {
        setOtpError("קוד אימות שגוי. אנא נסה שנית.")
        setFormState("otpError")
      } else {
        setFormState("otpVerified")
        // Final submission happens after OTP is verified
        setTimeout(() => {
          setFormState("success")
        }, 1000)
      }
    } catch (error: any) {
      console.error("Error confirming OTP:", error)

      // Increment attempt counter
      const newAttempts = otpAttempts + 1
      setOtpAttempts(newAttempts)

      // Check for 429 status code (quota exceeded) or if we've reached 3 attempts
      const isQuotaExceeded =
        error?.response?.status === 429 ||
        error?.response?.data?.includes?.("API calls quota exceeded") ||
        error?.response?.data?.includes?.("quota exceeded") ||
        error?.response?.data?.includes?.("maximum admitted")

      if (isQuotaExceeded || newAttempts >= 3) {
        setOtpError("השירות אינו זמין כרגע עקב עומס. אנא נסה שוב מאוחר יותר.")
        setFormState("otpError")
      } else {
        setOtpError("אירעה שגיאה באימות הקוד. אנא נסה שנית.")
        setFormState("otpError")
      }
    }
  }

  const handleResendOtp = async () => {
    try {
      setOtpError(null)

      if (
        !carDetails?.thirdPartyEligibilityClaimCheckId ||
        !accidentReportData
      ) {
        throw new Error("Missing data for resending OTP")
      }

      // Resend OTP by calling startClaimProcess again
      const startClaimRequest: StartClaimProcessRequest = {
        damageDescription: accidentReportData.damageDescription,
        email: accidentReportData.email,
        eventDescription: accidentReportData.eventDescription,
        identityNumber: accidentReportData.identityNumber,
        insuranceCompanyName: accidentReportData.insuranceCompanyName,
        licenseNumber,
        mobile: accidentReportData.mobile,
        policyNumberThirdParty: accidentReportData.policyNumberThirdParty,
        privateName: accidentReportData.privateName,
        sureName: accidentReportData.sureName,
        thirdPartyEligibilityClaimCheckId:
          carDetails.thirdPartyEligibilityClaimCheckId,
      }

      await startThirdPartyClaimProcess(startClaimRequest)

      setFormState("otpSent")
    } catch (error) {
      console.error("Error resending OTP:", error)
      setFormState("error")
    }
  }

  const handleRetry = () => {
    setFormState("idle")
    setCarDetails(null)
    setPhoneNumber("")
    setOtpError(null)
    setCarRepaired(null)
    setAccidentReportData(null)
    setLicenseNumber("")
    setEventDate("")
    setOtpAttempts(0) // Reset OTP attempts on retry
  }

  return {
    formState,
    carDetails,
    phoneNumber,
    otpError,
    carRepaired,
    accidentReportData,
    statusMessage,
    otpAttempts, // Expose attempts count
    handleCarDetailsSubmit,
    handleInsuranceDetailsSubmit,
    handleAccidentReportSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleRetry,
  }
}

export default useThirdPartyCoverageFlow
